import React, { useEffect, useState } from "react";
import { getUserToRequest } from "../manage-coaches/utils/manage-coaches-utils";
import { isCoach, isCoachFreeTrial } from "../../../../utils/user-helpers";
import { get } from "lodash";
import ToolTip from "../../../../parts/tooltip-portal";
import ThemeSelector from "./ThemeSelector";
import MultiOptionsInput from "./MultiOptionsInput";
import InputColor from "./InputColor";
import ImageSelector from "./ImageSelector";
import { GradientRange } from "../../../../parts/inputs/GradientRange";
import ImageSelectorSmall from "./ImageSelectorSmall";

export const MobileBrandingSection = ({
  active = false,
  state,
  locals,
  handleChange,
  handleSwitch,
  renderMobilePreview,
  renderMobileSplashPreview,
  onGradientRangeChange,
}) => {
  const {
    businessName,
    googlePlayLink,
    appStoreLink,
    appMainColor,
    isAppDefaultColor,
    isSplashImgTinted,
    logoImg = {},
    formLogoImg = {},
    mobileSplashImg = {},
    formMobileSplashImg,
    mobileSplashChanged,
    mobileSplashBg = {},
    formMobileSplashBg,
    mobileSplashBgChanged,
    splashOverlay,
    splashOverlayColor,
    splashOverlayOpacity,
    logoChanged,
    appTheme,
    appSecondaryTextColor,
    temporalSplashImg,
    temporalSplashBg,
  } = state;

  // * had to handle this with JS because if not we have duplicated inputs in the DOM
  // * and the inputs are not being updated correctly because they have the same name
  const [viewportWidth, setViewportWidth] = useState(null);

  useEffect(() => {
    !viewportWidth && setViewportWidth(window?.innerWidth || null);
    window.addEventListener("resize", updateDimensions);
    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  const updateDimensions = () => {
    setViewportWidth(window?.innerWidth || null);
  };

  const { activeUser, organizationOwner } = locals;
  const userToRequest = getUserToRequest(activeUser, organizationOwner);

  const athletesCount = parseInt(
    userToRequest && userToRequest.athletes && userToRequest.athletes.length
      ? userToRequest.athletes.length
      : 0
  );

  const userIsTrial = isCoachFreeTrial(userToRequest);
  const userIsCoach = isCoach(userToRequest);
  const enableCustomIcon = athletesCount >= 10 && !userIsTrial && userIsCoach;

  const userHasCustomIcon = get(userToRequest, "customMobileIcon", false);

  const userIsEnableToUpdateAppStoreListing = get(
    userToRequest,
    "appStoreListingEnable",
    false
  );
  const referAsClients = activeUser?.referAsClients;
  const customIconLabel = userHasCustomIcon
    ? "You Have a Custom App Icon"
    : athletesCount < 10
    ? `${10 - athletesCount} More ${referAsClients ? "Client" : "Athlete"}${
        athletesCount === 9 ? "" : "s"
      } Required`
    : "You are eligible for a custom app icon";
  return (
    <div
      style={{
        display: active ? "block" : "none",
      }}
      className="mobile-section"
    >
      <article>
        <p className="s1">Custom App Icon</p>
        <div className="flex gap-2">
          <div className={`chip ${userHasCustomIcon ? "info" : "warning"}`}>
            {customIconLabel}
          </div>
          <a
            role="button"
            data-button-type="outline"
            className={`custom-button${
              enableCustomIcon || userHasCustomIcon ? " active" : ""
            } h-8`}
            style={{
              cursor:
                enableCustomIcon || userHasCustomIcon ? "pointer" : "default",
              pointerEvents:
                enableCustomIcon || userHasCustomIcon ? "auto" : "none",
            }}
            href={
              userHasCustomIcon
                ? "https://form.typeform.com/to/Fm3TuJOF"
                : "https://form.typeform.com/to/teJvkEZE"
            }
            target="_blank"
            rel="noopener noreferrer"
            disabled={enableCustomIcon || userHasCustomIcon ? undefined : true}
          >
            <span>{userHasCustomIcon ? "Change Icon" : "Get Now"}</span>
          </a>
        </div>
      </article>
      <article>
        <header>
          <p className="s1">Custom AppStore Listing</p>
          <ToolTip body="Contact info@mystrengthbook.com for more details">
            <img
              alt="info icon"
              className="cursor-pointer"
              src="/img/new-icons/question-circle.svg"
            />
          </ToolTip>
        </header>
        <div className="flex gap-6">
          <label className="flex-grow">
            <span>AppStore link</span>
            <input
              type="text"
              name="appStoreLink"
              className="text-input"
              placeholder="Paste The Link Here"
              onChange={handleChange}
              value={appStoreLink}
              disabled={!userIsEnableToUpdateAppStoreListing}
              sz="large"
            />
          </label>
          <label className="flex-grow">
            <span>Google Play link</span>
            <input
              type="text"
              name="googlePlayLink"
              className="text-input"
              placeholder="Paste The Link Here"
              onChange={handleChange}
              value={googlePlayLink}
              disabled={!userIsEnableToUpdateAppStoreListing}
              sz="large"
            />
          </label>
        </div>
      </article>

      <article className="app-settings">
        <header>
          <p className="s1">App Settings</p>
        </header>
        <section>
          <div className="left-side">
            {viewportWidth && viewportWidth > 960 ? (
              <>
                <label>Business Name</label>
                <input
                  type="text"
                  name="businessName"
                  className="text-input"
                  placeholder="Business Name"
                  onChange={handleChange}
                  value={businessName}
                  sz="large"
                />
              </>
            ) : null}

            <label>Mobile App Theme</label>
            <ThemeSelector
              inputName="appTheme"
              label="App Theme"
              value={appTheme}
              onChange={handleSwitch}
              selectedBorder
            />
            <label>App Colors</label>
            <MultiOptionsInput
              value={isAppDefaultColor ? "default" : "custom"}
              onChange={(name, value) => {
                handleChange({
                  target: {
                    name,
                    value: value === "custom" ? false : true,
                  },
                });
              }}
              options={["custom", "default"]}
              inputName="isAppDefaultColor"
            />
            <label>App Main Color</label>
            <InputColor
              inputName="appMainColor"
              inputNameColor="appMainColorPicker"
              value={appMainColor}
              onChange={(evt) => {
                handleChange(evt);
              }}
            />
            <label>App Secondary Color (Text)</label>
            <MultiOptionsInput
              value={appSecondaryTextColor}
              onChange={(name, value) => {
                handleChange({
                  target: {
                    name,
                    value,
                  },
                });
              }}
              options={["white", "black"]}
              inputName="appSecondaryTextColor"
            />
            <label>Upload In-App Logo</label>
            <ImageSelectorSmall
              id="Logo"
              inputName="customBranding[logoImg]"
              imageInputs={[
                { name: "logoChanged", value: logoChanged },
                {
                  name: "formLogoImgFile",
                  value: formLogoImg.file || "",
                },
                {
                  name: "formLogoImgType",
                  value: formLogoImg.type || "",
                },
                {
                  name: "formLogoImgName",
                  value: formLogoImg.name || "",
                },
                {
                  name: "formLogoImgS3Path",
                  value: formLogoImg.s3Path,
                },
              ]}
              onChange={handleChange}
              value={logoImg}
            />
          </div>
          <div className="right-side">
            {viewportWidth && viewportWidth <= 960 ? (
              <div role="input-group">
                <label>Business Name</label>
                <input
                  type="text"
                  name="businessName"
                  className="text-input"
                  placeholder="Business Name"
                  onChange={handleChange}
                  value={businessName}
                  sz="large"
                />
              </div>
            ) : null}
            {renderMobilePreview()}
          </div>
        </section>
      </article>
      <article>
        <header>
          <p className="s1">Splash Screen Settings</p>
        </header>
        <section className="splash-screen">
          <div className="left-side">
            <div>
              <label>Splash Screen Logo</label>
              <ImageSelector
                id="Splash Screen Logo"
                temporalValue={temporalSplashImg}
                selectorTitle="Upload Logo"
                subInfo={
                  <div>
                    <span>JPG, GIF, SVG Or PNG.</span>
                    <span>Max Size: 800kb</span>
                  </div>
                }
                buttonTitle="Edit"
                // toolTipTextBody={
                //   "MSB will provide this asset for your storefront > how it works page"
                // }
                inputName="customBranding[mobileSplashImg]"
                imageInputs={[
                  {
                    name: "mobileSplashChanged",
                    value: mobileSplashChanged,
                  },
                  {
                    name: "formMobileSplashImgFile",
                    value: formMobileSplashImg.file || "",
                  },
                  {
                    name: "formMobileSplashImgType",
                    value: formMobileSplashImg.type || "",
                  },
                  {
                    name: "formMobileSplashImgName",
                    value: formMobileSplashImg.name || "",
                  },
                  {
                    name: "formMobileSplashImgS3Path",
                    value: formMobileSplashImg.s3Path,
                  },
                ]}
                value={mobileSplashImg}
                onChange={handleChange}
                additionalStyle={{
                  marginBottom: 16,
                  marginTop: 0,
                }}
                additionalContent={
                  <div className="switch-container">
                    <span>Tint Splashscreen Logo</span>
                    <input
                      name="tintSplashImg"
                      value={isSplashImgTinted}
                      type="checkbox"
                      role="switch"
                      onChange={() => {
                        handleChange({
                          target: {
                            name: "tintSplashImg",
                            value: !isSplashImgTinted,
                          },
                        });
                      }}
                      checked={isSplashImgTinted}
                      sz="large"
                    />
                  </div>
                }
                showButton={false}
              />
            </div>
            <div>
              <label>Splash Screen Image</label>
              <ImageSelector
                id="Splash Screen Image"
                temporalValue={temporalSplashBg}
                buttonTitle="Edit"
                additionalStyle={{
                  marginBottom: 16,
                  marginTop: 0,
                }}
                inputName="customBranding[mobileSplashBg]"
                showButton={false}
                imageInputs={[
                  {
                    name: "mobileSplashBgChanged",
                    value: mobileSplashBgChanged,
                  },
                  {
                    name: "formMobileSplashBgFile",
                    value: formMobileSplashBg.file || "",
                  },
                  {
                    name: "formMobileSplashBgType",
                    value: formMobileSplashBg.type || "",
                  },
                  {
                    name: "formMobileSplashBgName",
                    value: formMobileSplashBg.name || "",
                  },
                  {
                    name: "formMobileSplashBgS3Path",
                    value: formMobileSplashBg.s3Path,
                  },
                ]}
                value={mobileSplashBg}
                onChange={handleChange}
                selectorTitle="Upload Image"
                subInfo={
                  <div>
                    <span>JPG, GIF, SVG Or PNG.</span>
                    <span>Max Size: 800kb</span>
                  </div>
                }
                additionalContent={
                  <div>
                    <div className="switch-container">
                      <span>Overlay</span>
                      <input
                        name="splashOverlay"
                        value={splashOverlay}
                        type="checkbox"
                        role="switch"
                        onChange={() => {
                          handleChange({
                            target: {
                              name: "splashOverlay",
                              value: !splashOverlay,
                            },
                          });
                        }}
                        checked={splashOverlay}
                        sz="large"
                      />
                    </div>
                    {splashOverlay
                      ? [
                          <InputColor
                            inputName="splashOverlayColor"
                            inputNameColor="splashOverlayColorPicker"
                            value={splashOverlayColor}
                            onChange={handleChange}
                          />,

                          <div>
                            <div className="flex items-center justify-between mt-4">
                              <p className="p4">Opacity</p>
                              <span>{splashOverlayOpacity}</span>
                            </div>

                            <GradientRange
                              onChange={onGradientRangeChange}
                              rangeName={"splashOverlayOpacity"}
                              rangeValue={splashOverlayOpacity}
                            />
                          </div>,
                        ]
                      : null}
                  </div>
                }
              />
            </div>
          </div>
          <div className="right-side">{renderMobileSplashPreview()}</div>
        </section>
      </article>
    </div>
  );
};
